import React, { useEffect, useMemo, useState } from 'react';
import axios from '../../axiosConfig';
import { Button, Form, Table, Spinner } from 'react-bootstrap';
import { saveAs } from 'file-saver';
import * as XLSX from 'xlsx';
import { FaSearch, FaSync } from 'react-icons/fa';
import GraficaBarras from '../../components/GraficaBarras';
import { MarcaSiNo } from '../../components/SiNo';
import { puedeVerModulo } from '../../components/accesoModulos';
import { useNavigate } from 'react-router-dom';
import estadosMunicipios from '../../components/estadosMunicipios.json';

// Red de egresados (punto 14 de José, 24/09/2026). Personas que egresaron en
// un centro marcado como "Red de egresados", con 17 años o más y 24 horas o
// más sumadas entre esos cursos. Se calcula sola en el backend
// (RedEgresadosController): quien egresa y cumple aparece sin cargarlo.

const FILTROS_VACIOS = {
  cedula_identidad: '', nombres: '', apellidos: '',
  centro_id: '', cohorte: '', periodo_id: '', area_id: '', nombre_uc: '',
  genero_id: '', estado_id: '', municipio: '', procedencia_id: '',
  grupo_prioritario_id: '', nivel_instruccion_id: '',
  edad_mayor: '', edad_menor: '', horas_minimas: '',
  // Seguimiento de Egresados (punto 7 de José, 25/09/2026)
  con_seguimiento: '', aplica_red: '', trabaja: '', emprende: '', actividad_interes_id: '',
};

// Opciones ✓ / ✗ / sin dato con la etiqueta delante, para que el filtro se
// entienda también cuando está seleccionado ("Trabaja: ✓").
const siNoSin = (etiqueta) => [
  { valor: 'si', texto: `${etiqueta}: ✓` },
  { valor: 'no', texto: `${etiqueta}: ✗` },
  { valor: 'sin', texto: `${etiqueta}: sin dato` },
];

const textoSiNo = (v) => (v === true ? 'Sí' : v === false ? 'No' : '');

const rangosComoFilas = (rangos, total) =>
  Object.entries(rangos || {}).map(([nombre, cantidad]) => ({
    nombre, cantidad, porcentaje: total > 0 ? Math.round((cantidad * 10000) / total) / 100 : 0,
  }));

const Tarjeta = ({ titulo, valor, detalle, color }) => (
  <div className="chart-box text-center" style={{ flex: '1 1 0', minWidth: '200px' }}>
    <div style={{ fontSize: '2rem', fontWeight: 700, color }}>{valor}</div>
    <div style={{ color: 'gray' }}>{titulo}</div>
    {detalle && <div className="small text-muted">{detalle}</div>}
  </div>
);

const ShowRedEgresados = () => {
  const navigate = useNavigate();
  // La ficha de seguimiento es solo para admin y usuarios puntuales (accesoModulos.js).
  const verSeguimiento = puedeVerModulo('seguimiento-egresados');
  const [actividades, setActividades] = useState([]);
  const [filtros, setFiltros] = useState(FILTROS_VACIOS);
  const [pagina, setPagina] = useState(1);
  const [datos, setDatos] = useState(null);
  const [opciones, setOpciones] = useState({});
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState('');

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };
  const limpios = (f) => Object.fromEntries(Object.entries(f).filter(([, v]) => v !== ''));

  const cargar = async (f = filtros, p = 1) => {
    setCargando(true);
    setError('');
    try {
      const { data } = await axios.get('/red-egresados', { headers, params: { ...limpios(f), page: p } });
      setDatos(data);
      setPagina(p);
    } catch (e) {
      console.error('Error cargando la Red de egresados:', e);
      setError('No se pudo cargar la Red de egresados. Intente de nuevo.');
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => {
    // Los catálogos de los filtros son los mismos de Indicadores.
    axios.get('/filter-indicadores', { headers })
      .then(({ data }) => setOpciones(data))
      .catch((e) => console.error('Error cargando filtros:', e));
    // Actividades de interés del Seguimiento de Egresados (para el filtro).
    axios.get('/seguimiento-egresados-opciones', { headers })
      .then(({ data }) => setActividades(data.actividades || []))
      .catch(() => setActividades([]));
    cargar(FILTROS_VACIOS, 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const municipios = useMemo(() => {
    const est = estadosMunicipios.find((e) => String(e.id_estado) === String(filtros.estado_id));
    return est ? est.municipios : [];
  }, [filtros.estado_id]);

  const cambiar = (e) => {
    const { name, value } = e.target;
    setFiltros((prev) => ({ ...prev, [name]: value, ...(name === 'estado_id' ? { municipio: '' } : {}) }));
  };
  const limpiar = () => { setFiltros(FILTROS_VACIOS); cargar(FILTROS_VACIOS, 1); };
  const activo = (v) => (v ? 'is-active-filter' : '');

  const exportar = async () => {
    try {
      const { data } = await axios.get('/red-egresados-print', { headers, params: limpios(filtros) });
      const filas = (data.datos || []).map((p) => ({
        'Cédula': p.cedula_identidad, Nombres: p.nombres, Apellidos: p.apellidos, Edad: p.edad,
        'Género': p.genero, 'Teléfono': p.telefono_celular, Email: p.direccion_email,
        'Centros de la Red': p.centros, 'Cursos egresados': p.cursos_egresados, 'Horas acumuladas': Number(p.horas),
        'Aplica a la Red': textoSiNo(p.aplica_red),
        'Nivel de instrucción actual': p.nivel_instruccion_actual || '',
        Trabaja: textoSiNo(p.trabaja), Empresa: p.empresa || '', Cargo: p.cargo || '',
        'Trabaja en su área': textoSiNo(p.trabaja_en_area),
        Emprende: textoSiNo(p.emprende), Emprendimiento: p.emprendimiento || '',
        'Actividades de interés': p.actividades_interes || '',
        Comentarios: p.comentarios || '',
        'Seguimiento actualizado': p.seguimiento_actualizado || '',
      }));
      const libro = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(libro, XLSX.utils.json_to_sheet(filas), 'Red de egresados');
      const e = datos?.estadisticas;
      if (e) {
        const resumen = [
          { Indicador: 'Personas en la Red', Valor: e.totalPersonas },
          { Indicador: 'Horas promedio', Valor: e.horasPromedio },
          ...e.porCentro.map((x) => ({ Indicador: `Centro: ${x.nombre}`, Valor: x.cantidad })),
          ...e.porGenero.map((x) => ({ Indicador: `Género: ${x.nombre}`, Valor: x.cantidad })),
          ...rangosComoFilas(e.rangoEdades, e.totalPersonas).map((x) => ({ Indicador: `Edad: ${x.nombre}`, Valor: x.cantidad })),
          { Indicador: 'Personas con seguimiento', Valor: e.conSeguimiento },
          ...(e.porAplicaRed || []).map((x) => ({ Indicador: `Aplica a la Red: ${x.nombre}`, Valor: x.cantidad })),
          ...(e.porTrabaja || []).map((x) => ({ Indicador: `Laboral: ${x.nombre}`, Valor: x.cantidad })),
          ...(e.porTrabajaEnArea || []).map((x) => ({ Indicador: `Trabaja en su área: ${x.nombre}`, Valor: x.cantidad })),
          ...(e.porEmprende || []).map((x) => ({ Indicador: `Emprendimiento: ${x.nombre}`, Valor: x.cantidad })),
          ...(e.porNivelInstruccionActual || []).map((x) => ({ Indicador: `Nivel actual: ${x.nombre}`, Valor: x.cantidad })),
          ...(e.porActividadInteres || []).map((x) => ({ Indicador: `Actividad de interés: ${x.nombre}`, Valor: x.cantidad })),
        ];
        XLSX.utils.book_append_sheet(libro, XLSX.utils.json_to_sheet(resumen), 'Indicadores');
      }
      const buffer = XLSX.write(libro, { bookType: 'xlsx', type: 'array' });
      saveAs(new Blob([buffer], { type: 'application/octet-stream' }), 'red_de_egresados.xlsx');
    } catch (e) {
      console.error('Error exportando:', e);
      setError('No se pudo exportar a Excel.');
    }
  };

  const e = datos?.estadisticas;
  const c = datos?.criterios || { edadMinima: 17, horasMinimas: 24 };
  const lista = datos?.datos?.data || [];
  const ultima = datos?.datos?.last_page || 1;
  const sel = (name, placeholder, items, valor = 'id', texto = 'descripcion') => (
    <Form.Select name={name} value={filtros[name]} onChange={cambiar} className={activo(filtros[name])} style={{ width: 'auto', maxWidth: '240px' }}>
      <option value="">{placeholder}</option>
      {(items || []).map((o) => <option key={o[valor]} value={o[valor]}>{o[texto]}</option>)}
    </Form.Select>
  );

  return (
    <div className="row" style={{ marginTop: '40px' }}>
      <div className="col-lg-12">
        <div className="card-box" style={{ padding: '15px' }}>
          <div className="d-flex justify-content-between align-items-center mb-2 flex-wrap" style={{ gap: '10px' }}>
            <h2 style={{ fontSize: '1.8rem' }} className="mb-0">Red de Egresados</h2>
            <div className="d-flex" style={{ gap: '8px' }}>
              <Button variant="secondary" onClick={limpiar} title="Quitar filtros"><FaSync /></Button>
              <Button variant="success" onClick={exportar} disabled={!datos} title="Exportar a Excel">
                <i className="bi bi-printer-fill"></i>
              </Button>
            </div>
          </div>
          <p className="small text-muted mb-3">
            Personas que egresaron en un centro de la Red, con {c.edadMinima} años o más y {c.horasMinimas} horas
            o más sumadas entre esos cursos. Se actualiza sola: cuando alguien egresa y cumple, aparece aquí.
            Los centros de la Red se marcan en Maestros &gt; Centros.
          </p>

          <div className="d-flex flex-wrap align-items-center mb-2" style={{ gap: '10px' }}>
            <Form.Control name="cedula_identidad" value={filtros.cedula_identidad} onChange={cambiar} placeholder="Cédula" style={{ width: '150px' }} className={activo(filtros.cedula_identidad)} />
            <Form.Control name="nombres" value={filtros.nombres} onChange={cambiar} placeholder="Nombres" style={{ width: '170px' }} className={activo(filtros.nombres)} />
            <Form.Control name="apellidos" value={filtros.apellidos} onChange={cambiar} placeholder="Apellidos" style={{ width: '170px' }} className={activo(filtros.apellidos)} />
            <Form.Control name="nombre_uc" value={filtros.nombre_uc} onChange={cambiar} placeholder="Nombre de Programa" style={{ width: '200px' }} className={activo(filtros.nombre_uc)} />
          </div>
          <div className="d-flex flex-wrap align-items-center mb-2" style={{ gap: '10px' }}>
            {sel('centro_id', 'Centro de la Red', datos?.centrosRed)}
            {sel('cohorte', 'Cohorte', opciones.cohorte, 'descripcion')}
            {sel('periodo_id', 'Período', opciones.periodo)}
            {sel('area_id', 'Área', opciones.area)}
            {sel('genero_id', 'Género', opciones.genero)}
            {sel('estado_id', 'Estado', opciones.estado)}
            <Form.Select name="municipio" value={filtros.municipio} onChange={cambiar} disabled={!municipios.length} className={activo(filtros.municipio)} style={{ width: 'auto', maxWidth: '220px' }}>
              <option value="">Municipio</option>
              {municipios.map((m) => <option key={m.municipio} value={m.municipio}>{m.municipio}</option>)}
            </Form.Select>
            {sel('procedencia_id', 'Procedencia', opciones.procedencia)}
            {sel('grupo_prioritario_id', 'Grupo prioritario', opciones.grupo_prioritario)}
            {sel('nivel_instruccion_id', 'Nivel de instrucción', opciones.nivelInstruccion)}
          </div>
          <div className="d-flex flex-wrap align-items-center mb-2" style={{ gap: '10px' }}>
            <span className="small text-muted">Seguimiento:</span>
            <Form.Select name="con_seguimiento" value={filtros.con_seguimiento} onChange={cambiar} className={activo(filtros.con_seguimiento)} style={{ width: 'auto' }}>
              <option value="">Con o sin seguimiento</option>
              <option value="si">Con seguimiento</option>
              <option value="no">Sin seguimiento</option>
            </Form.Select>
            {sel('aplica_red', 'Aplica a la Red', siNoSin('Aplica'), 'valor', 'texto')}
            {sel('trabaja', 'Trabaja', siNoSin('Trabaja'), 'valor', 'texto')}
            {sel('emprende', 'Emprende', siNoSin('Emprende'), 'valor', 'texto')}
            {sel('actividad_interes_id', 'Actividad de interés', actividades)}
          </div>
          <div className="d-flex flex-wrap align-items-center" style={{ gap: '10px' }}>
            <Form.Control type="number" name="edad_mayor" value={filtros.edad_mayor} onChange={cambiar} placeholder="Edad desde" style={{ width: '130px' }} className={activo(filtros.edad_mayor)} />
            <Form.Control type="number" name="edad_menor" value={filtros.edad_menor} onChange={cambiar} placeholder="Edad hasta" style={{ width: '130px' }} className={activo(filtros.edad_menor)} />
            <Form.Control type="number" name="horas_minimas" value={filtros.horas_minimas} onChange={cambiar} placeholder={`Horas desde (mín. ${c.horasMinimas})`} style={{ width: '200px' }} className={activo(filtros.horas_minimas)} />
            <Button variant="info" onClick={() => cargar(filtros, 1)} disabled={cargando}>
              {cargando ? <Spinner size="sm" animation="border" /> : <><FaSearch /> Buscar</>}
            </Button>
          </div>
          {error && <div className="text-danger small mt-2">{error}</div>}
          {datos && datos.centrosRed?.length === 0 && (
            <div className="alert alert-warning mt-3 mb-0">
              Todavía no hay ningún centro marcado como parte de la Red de egresados. Márquelos en
              Maestros &gt; Centros &gt; Editar &gt; "¿Pertenece a la Red de egresados?".
            </div>
          )}
        </div>

        {datos && e && (
          <>
            <div className="d-flex flex-wrap mt-3" style={{ gap: '20px' }}>
              <Tarjeta titulo="Personas en la Red" valor={e.totalPersonas} color="#0d6efd" />
              <Tarjeta titulo="Horas promedio" valor={e.horasPromedio} detalle="sumadas entre sus cursos en la Red" color="#6f42c1" />
              <Tarjeta titulo="Centros de la Red" valor={datos.centrosRed?.length || 0} color="green" />
              <Tarjeta titulo="Con seguimiento" valor={e.conSeguimiento ?? 0}
                detalle="fichas cargadas en Seguimiento Egresados" color="#fd7e14" />
            </div>

            <div className="chart-box mt-3" style={{ width: '100%', overflowX: 'auto' }}>
              <h4 style={{ fontSize: '1.2rem' }}>Personas</h4>
              <Table size="sm" bordered hover className="mb-2">
                <thead>
                  <tr><th>Cédula</th><th>Nombres</th><th>Apellidos</th><th className="text-end">Edad</th><th>Género</th>
                    <th>Teléfono</th><th>Email</th><th>Centros</th><th className="text-end">Cursos</th><th className="text-end">Horas</th>
                    <th className="text-center">Aplica</th><th className="text-center">Trabaja</th><th className="text-center">Emprende</th><th></th></tr>
                </thead>
                <tbody>
                  {lista.length === 0 && <tr><td colSpan={14} className="text-muted">Nadie cumple los criterios con los filtros aplicados.</td></tr>}
                  {lista.map((p) => (
                    <tr key={p.id}>
                      <td>{p.cedula_identidad}</td><td>{p.nombres}</td><td>{p.apellidos}</td>
                      <td className="text-end">{p.edad}</td><td>{p.genero}</td>
                      <td>{p.telefono_celular}</td><td>{p.direccion_email}</td><td>{p.centros}</td>
                      <td className="text-end">{p.cursos_egresados}</td><td className="text-end">{Number(p.horas)}</td>
                      <td className="text-center"><MarcaSiNo valor={p.aplica_red} si="Aplica" no="No aplica" /></td>
                      <td className="text-center"><MarcaSiNo valor={p.trabaja} /></td>
                      <td className="text-center"><MarcaSiNo valor={p.emprende} /></td>
                      <td>
                        {verSeguimiento && (
                          <Button size="sm" variant="outline-primary" title="Ver seguimiento"
                            onClick={() => navigate(`/seguimiento-egresados/${p.id}`)}>
                            <i className="bi bi-eye-fill"></i>
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
              <div className="d-flex justify-content-between align-items-center">
                <Button size="sm" variant="secondary" disabled={pagina <= 1 || cargando} onClick={() => cargar(filtros, pagina - 1)}>Anterior</Button>
                <span className="small text-muted">Página {pagina} de {ultima} · {datos.datos?.total || 0} personas</span>
                <Button size="sm" variant="secondary" disabled={pagina >= ultima || cargando} onClick={() => cargar(filtros, pagina + 1)}>Siguiente</Button>
              </div>
            </div>

            {/* Contenedores flex y no .row/.col: EditVoluntariados.css redefine
                .row para toda la aplicación y rompe la grilla de Bootstrap. */}
            <div className="d-flex flex-wrap mt-3" style={{ gap: '20px' }}>
              <div style={{ flex: '1 1 calc(50% - 10px)', minWidth: 0 }}>
                <GraficaBarras titulo="Por Centro" datos={e.porCentro} color="#8884d8"
                  nota="Quien egresó en dos centros de la Red cuenta en los dos." />
              </div>
              <div style={{ flex: '1 1 calc(50% - 10px)', minWidth: 0 }}>
                <GraficaBarras titulo="Horas acumuladas" datos={e.porHoras} color="#6f42c1" />
              </div>
              <div style={{ flex: '1 1 calc(50% - 10px)', minWidth: 0 }}>
                <GraficaBarras titulo="Por Género" datos={e.porGenero} color="#ffc658" />
              </div>
              <div style={{ flex: '1 1 calc(50% - 10px)', minWidth: 0 }}>
                <GraficaBarras titulo="Por Rango de Edad" datos={rangosComoFilas(e.rangoEdades, e.totalPersonas)} color="#82ca9d" />
              </div>
              <div style={{ flex: '1 1 calc(50% - 10px)', minWidth: 0 }}>
                <GraficaBarras titulo="Por Estado" datos={e.porEstado} color="#00C49F" />
              </div>
              <div style={{ flex: '1 1 calc(50% - 10px)', minWidth: 0 }}>
                <GraficaBarras titulo="Por Procedencia" datos={e.porProcedencia} color="#ff7300" />
              </div>
              <div style={{ flex: '1 1 calc(50% - 10px)', minWidth: 0 }}>
                <GraficaBarras titulo="Por Grupo Prioritario" datos={e.porGrupoPrioritario} color="#8884d8" />
              </div>
              <div style={{ flex: '1 1 calc(50% - 10px)', minWidth: 0 }}>
                <GraficaBarras titulo="Por Nivel de Instrucción" datos={e.porNivelInstruccion} color="#e8743b" />
              </div>
            </div>

            {/* Indicadores del Seguimiento de Egresados (punto 7, 25/09/2026). */}
            <h4 className="mt-4 mb-0" style={{ fontSize: '1.3rem' }}>Seguimiento de egresados</h4>
            <div className="small text-muted">
              {e.conSeguimiento ?? 0} de {e.totalPersonas} personas tienen ficha de seguimiento. "Sin dato" son las que
              todavía no la tienen o no respondieron.
            </div>
            <div className="d-flex flex-wrap mt-2" style={{ gap: '20px' }}>
              <div style={{ flex: '1 1 calc(50% - 10px)', minWidth: 0 }}>
                <GraficaBarras titulo="¿Aplica a la Red?" datos={e.porAplicaRed} color="#198754" />
              </div>
              <div style={{ flex: '1 1 calc(50% - 10px)', minWidth: 0 }}>
                <GraficaBarras titulo="Situación laboral" datos={e.porTrabaja} color="#0d6efd" />
              </div>
              <div style={{ flex: '1 1 calc(50% - 10px)', minWidth: 0 }}>
                <GraficaBarras titulo="Trabaja en su área" datos={e.porTrabajaEnArea} color="#20c997" />
              </div>
              <div style={{ flex: '1 1 calc(50% - 10px)', minWidth: 0 }}>
                <GraficaBarras titulo="Emprendimiento" datos={e.porEmprende} color="#fd7e14" />
              </div>
              <div style={{ flex: '1 1 calc(50% - 10px)', minWidth: 0 }}>
                <GraficaBarras titulo="Nivel de instrucción actual" datos={e.porNivelInstruccionActual} color="#e8743b"
                  nota="Solo personas con ficha de seguimiento." />
              </div>
              <div style={{ flex: '1 1 calc(50% - 10px)', minWidth: 0 }}>
                <GraficaBarras titulo="Actividades de interés" datos={e.porActividadInteres} color="#6f42c1"
                  nota="Cada persona puede elegir varias: la suma puede superar al total." />
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ShowRedEgresados;
